import { createListenerMiddleware } from '@reduxjs/toolkit';
import { getAmlGeneralInfo, getAmlGeneralInfoSuccess } from '../slices/aml/aml';
import {
  getCards,
  getCardsSuccess,
  onCardStatusChange,
  onCardStatusChangeSuccess,
  onSubmitCard,
  onSubmitCardSuccess,
  getCardInfo,
  getCardInfoSuccess,
} from '../slices/cards/cards';
import {
  CardStatus,
  CustomerCard,
  CardType,
  CardPaymentSystem,
} from '../../../customers/types';
import {
  CorporateAmlGeneralInfo,
  CorporateCustomerEntityForm,
} from '../../types';
import { CustomersRootState } from '../../../../store/state';
import getCardAvailableStatuses from '../../../../helpers/get-card-available-statuses';

const amlGeneralInfoMock: CorporateAmlGeneralInfo = {
  entityForm: CorporateCustomerEntityForm.LTD,
  sic: '58110. Book publishing',
  mcc: '6938',
  description: `A & C Black Publishers Ltd. publishes books. The Company publishes business books, children's books, educational books, reference books, and books on drama, music, natural history, sports, fitness, the visual arts, and writing. A & C Black Publishers offers its products throughout the world.`,
  nextReviewDate: new Date('2022-08-10').toISOString(),
};

const corporateCustomerCardsMock: CustomerCard[] = [
  {
    id: 'uuid-1',
    paymentSystem: CardPaymentSystem.VISA,
    number: '**57 8478',
    type: CardType.CORP,
    expiredDate: new Date('2028-01-04').toISOString(),
    balance: '587.54',
    balanceCurrency: 'eur',
    status: CardStatus.NEW,
    customerAccountId: '3.0.00.000001',
    address: 'United Kingdom, London, Apt. 203, 23, Baker street, W1 5DE',
    customerFirstName: 'James',
    customerLastName: 'Bond',
    virtual: false,
  },
  {
    id: 'uuid-2',
    paymentSystem: CardPaymentSystem.MASTER_CARD,
    number: '**57 8478',
    type: CardType.CORP,
    expiredDate: new Date('2028-01-04').toISOString(),
    balance: undefined,
    balanceCurrency: 'eur',
    status: CardStatus.ORDERED,
    customerAccountId: '3.0.00.000001',
    address: 'United Kingdom, London, Apt. 203, 23, Baker street, W1 5DE',
    customerFirstName: 'James',
    customerLastName: 'Bond',
    virtual: true,
  },
  {
    id: 'uuid-3',
    paymentSystem: CardPaymentSystem.MASTER_CARD,
    number: '**57 8478',
    type: CardType.CORP,
    expiredDate: new Date('2028-01-04').toISOString(),
    balance: '587.54',
    balanceCurrency: 'eur',
    status: CardStatus.ACTIVE,
    customerAccountId: '3.0.00.000001',
    address: 'United Kingdom, London, Apt. 203, 23, Baker street, W1 5DE',
    customerFirstName: 'James',
    customerLastName: 'Bond',
    virtual: false,
  },
  {
    id: 'uuid-4',
    paymentSystem: CardPaymentSystem.VISA,
    number: '**57 8478',
    type: CardType.CORP,
    expiredDate: new Date('2028-01-04').toISOString(),
    balance: '587.54',
    balanceCurrency: 'eur',
    status: CardStatus.FROZEN,
    customerAccountId: '3.0.00.000001',
    address: 'United Kingdom, London, Apt. 203, 23, Baker street, W1 5DE',
    customerFirstName: 'James',
    customerLastName: 'Bond',
    virtual: false,
  },
  {
    id: 'uuid-5',
    paymentSystem: CardPaymentSystem.VISA,
    number: '**57 8478',
    type: CardType.CORP,
    expiredDate: new Date('2028-01-04').toISOString(),
    balance: '587.54',
    balanceCurrency: 'eur',
    status: CardStatus.CANCELED,
    customerAccountId: '3.0.00.000001',
    address: 'United Kingdom, London, Apt. 203, 23, Baker street, W1 5DE',
    customerFirstName: 'James',
    customerLastName: 'Bond',
    virtual: false,
  },
  {
    id: 'uuid-6',
    paymentSystem: CardPaymentSystem.VISA,
    number: '**57 8478',
    type: CardType.CORP,
    expiredDate: new Date('2028-01-04').toISOString(),
    balance: '587.54',
    balanceCurrency: 'eur',
    status: CardStatus.VOID,
    customerAccountId: '3.0.00.000001',
    address: 'United Kingdom, London, Apt. 203, 23, Baker street, W1 5DE',
    customerFirstName: 'James',
    customerLastName: 'Bond',
    virtual: true,
  },
];

const corporateCustomerMiddleware = createListenerMiddleware();

corporateCustomerMiddleware.startListening({
  actionCreator: getAmlGeneralInfo,
  effect: async (action, listenerApi) => {
    setTimeout(() => {
      listenerApi.dispatch(getAmlGeneralInfoSuccess(amlGeneralInfoMock));
      listenerApi.cancelActiveListeners();
    }, 1000);
  },
});

corporateCustomerMiddleware.startListening({
  actionCreator: getCards,
  effect: async (action, listenerApi) => {
    const nextCards = corporateCustomerCardsMock.map((item) => {
      const result = { ...item };

      result.availableStatuses = getCardAvailableStatuses(item.status);

      return result;
    });

    setTimeout(() => {
      listenerApi.dispatch(getCardsSuccess(nextCards));
      listenerApi.cancelActiveListeners();
    }, 1000);
  },
});

corporateCustomerMiddleware.startListening({
  actionCreator: onSubmitCard,
  effect: async (action, listenerApi) => {
    const {
      corporateCustomers: {
        info: {
          cards: { cards, orderCardFormValues },
        },
      },
    } = listenerApi.getState() as CustomersRootState;

    const nextCards = ([] as CustomerCard[]).concat(cards as CustomerCard[]);

    let card: CustomerCard = {
      id: `uuid-${nextCards.length + 1}`,
      paymentSystem: orderCardFormValues?.paymentSystem as CardPaymentSystem,
      number: '**57 8478',
      type: CardType.CORP,
      expiredDate: new Date('2028-01-04').toISOString(),
      balanceCurrency: 'eur',
      status: CardStatus.ORDERED,
      customerAccountId: '3.0.00.000001',
      address: '',
      customerFirstName: 'James',
      customerLastName: 'Bond',
      availableStatuses: getCardAvailableStatuses(CardStatus.ORDERED),
      virtual: !orderCardFormValues?.physical,
    };

    if (orderCardFormValues?.physical) {
      card = {
        ...card,
        address:
          `${orderCardFormValues.countryName}, ${orderCardFormValues.city}, ${orderCardFormValues.apartment}, ${orderCardFormValues.street}, ${orderCardFormValues.postCode}`,
        countryId: orderCardFormValues?.countryId,
        city: orderCardFormValues?.city,
        postCode: orderCardFormValues?.postCode,
        street: orderCardFormValues?.street,
        apartment: orderCardFormValues?.apartment,
      };
    }

    nextCards.push(card);

    setTimeout(() => {
      listenerApi.dispatch(onSubmitCardSuccess(nextCards));
      listenerApi.cancelActiveListeners();
    }, 1000);
  },
});

corporateCustomerMiddleware.startListening({
  actionCreator: getCardInfo,
  effect: async (action, listenerApi) => {
    const {
      corporateCustomers: {
        info: {
          cards: { cards, cardId },
        },
      },
    } = listenerApi.getState() as CustomersRootState;

    const currentCard = cards?.find((item) => item.id === cardId);

    setTimeout(() => {
      listenerApi.dispatch(getCardInfoSuccess(currentCard as CustomerCard));
      listenerApi.cancelActiveListeners();
    }, 1000);
  },
});

corporateCustomerMiddleware.startListening({
  actionCreator: onCardStatusChange,
  effect: async (action, listenerApi) => {
    const {
      corporateCustomers: {
        info: {
          cards: { cards },
        },
      },
    } = listenerApi.getState() as CustomersRootState;

    const nextCards = cards?.map((item) => {
      const result = { ...item };

      if (result.id === action.payload.id) {
        result.status = action.payload.status;
        result.availableStatuses = getCardAvailableStatuses(
          action.payload.status,
        );
      }

      return result;
    });

    setTimeout(() => {
      listenerApi.dispatch(
        onCardStatusChangeSuccess(nextCards as CustomerCard[]),
      );
      listenerApi.cancelActiveListeners();
    }, 1000);
  },
});

export default corporateCustomerMiddleware;
